import React, {ReactNode, createContext, useCallback, useContext, useEffect, useRef, useState} from 'react'
import {Box, Collapse, useMultiStyleConfig, BoxProps} from '@chakra-ui/react'
import {CustomStyle} from '_uiComponents'

/**
 * Signals to descendant Page Designer modules that they are being rendered as the revealed content of
 * a `32_accordion`. Child modules (e.g. `12_titleTextCta`) read this to adapt their layout to the
 * accordion context (full-width text instead of their standalone fixed width) WITHOUT the accordion
 * theme having to override the child's styles with nested selectors / `!important`.
 */
const AccordionContext = createContext(false)

/** True when the current component is rendered inside a `32_accordion` revealed panel. */
export const useIsInsideAccordion = (): boolean => useContext(AccordionContext)

/**
 * Custom DOM event dispatched by an external CTA (e.g. a `12_titleTextCta` whose `cta_url` equals an
 * accordion `moduleId`) to open/toggle the matching `32_accordion` module. Kept as a document-level
 * event so the trigger and the accordion stay decoupled: the CTA does not need a ref/context to the
 * accordion, it just broadcasts the target moduleId (mirrors the SFRA Bootstrap-collapse behaviour
 * where the CTA targets the collapse by id).
 */
export const PD_ACCORDION_TOGGLE_EVENT = 'pd:accordion:toggle'

/**
 * Broadcast by the accordion whenever its open state changes (and once on mount), so an external
 * trigger CTA can reflect that state (e.g. flip its chevron up/down). Carries `{moduleId, open}`.
 */
export const PD_ACCORDION_STATE_EVENT = 'pd:accordion:state'

/** Marks the accordion root in the DOM so an external CTA can detect a matching accordion exists. */
export const PD_ACCORDION_MODULE_ATTR = 'data-accordion-module-id'

export interface AccordionPdProps {
    /** Unique HTML id / collapse target (Page Designer `moduleId`). An external CTA opens this accordion by matching its `cta_url` to this value. */
    moduleId: string
    /** Additional CSS class names from Page Designer (`class_name`). */
    classes?: string
    /** Custom CSS authored in Page Designer (`custom_css`). */
    extraCSS?: string
    /** Accordion children (the `accordionItems` region), already rendered via `renderPageDesignerRegion`. */
    children?: ReactNode
}

/**
 * @name AccordionPd
 *
 * @description
 * Page Designer layout module `32_accordion`: a collapsible container that wraps arbitrary child
 * modules. It has NO header/arrow of its own — it starts collapsed and is opened by an EXTERNAL CTA
 * (another Page Designer module whose `cta_url` equals this accordion's `moduleId`). This mirrors the
 * SFRA implementation (Bootstrap `collapse` targeted by id) rather than a self-contained accordion.
 *
 * The children are rendered by the caller through `renderPageDesignerRegion` and passed in as
 * `children`, so any Page Designer component is supported inside.
 *
 * Opening is driven by a document-level `PD_ACCORDION_TOGGLE_EVENT` carrying the target `moduleId`,
 * which the CTA dispatches (see `useOpenDrawer.handleAccordionClick`). On open the accordion scrolls
 * itself into view, matching the "jump to the revealed content" behaviour of the reference storefront.
 */
const AccordionPd = ({moduleId, classes, extraCSS, children}: AccordionPdProps): JSX.Element => {
    const styles = useMultiStyleConfig('AccordionPd') as Record<string, BoxProps>
    const [isOpen, setIsOpen] = useState(false)
    const rootRef = useRef<HTMLDivElement>(null)

    const handleToggle = useCallback(
        (event: Event) => {
            const detail = (event as CustomEvent<{moduleId?: string; open?: boolean}>).detail
            if (!detail || detail.moduleId !== moduleId) return

            setIsOpen((prev) => (typeof detail.open === 'boolean' ? detail.open : !prev))
        },
        [moduleId],
    )

    useEffect(() => {
        if (typeof document === 'undefined') return
        document.addEventListener(PD_ACCORDION_TOGGLE_EVENT, handleToggle)
        return () => document.removeEventListener(PD_ACCORDION_TOGGLE_EVENT, handleToggle)
    }, [handleToggle])

    // Scroll the revealed content into view when the accordion opens (parity with the SFRA anchor jump).
    useEffect(() => {
        if (isOpen && rootRef.current) {
            rootRef.current.scrollIntoView({behavior: 'smooth', block: 'nearest'})
        }
    }, [isOpen])

    // Broadcast the current state so an external trigger CTA can reflect it (chevron up/down). Fires on
    // mount too, so a CTA that renders before/after the accordion still learns the initial state.
    useEffect(() => {
        if (typeof document === 'undefined') return
        document.dispatchEvent(new CustomEvent(PD_ACCORDION_STATE_EVENT, {detail: {moduleId, open: isOpen}}))
    }, [isOpen, moduleId])

    return (
        <Box
            {...styles.wrapper}
            ref={rootRef}
            data-testid="pd-accordion"
            {...{[PD_ACCORDION_MODULE_ATTR]: moduleId}}
            id={moduleId}
            className={`${classes ?? ''} cc-pd cc-pd-accordion`}
        >
            <CustomStyle css={extraCSS} />
            <Collapse in={isOpen} animateOpacity>
                <Box {...styles.panel} className="cc-pd-accordion-wrp">
                    <AccordionContext.Provider value={true}>{children}</AccordionContext.Provider>
                </Box>
            </Collapse>
        </Box>
    )
}

export {AccordionPd}
